import { useState } from 'react';
import type { AdminUser, MembershipPlanType } from '../types/api';

interface GrantMembershipDialogProps {
  user: AdminUser;
  onConfirm: (planType: MembershipPlanType, months: number) => void;
  onCancel: () => void;
  isPending: boolean;
}

export default function GrantMembershipDialog({
  user,
  onConfirm,
  onCancel,
  isPending,
}: GrantMembershipDialogProps) {
  const [planType, setPlanType] = useState<MembershipPlanType>('monthly');
  const [months, setMonths] = useState('1');

  const parsedMonths = Number(months);
  const monthsValid =
    Number.isInteger(parsedMonths) && parsedMonths >= 1 && parsedMonths <= 120;
  const isValid = planType === 'lifetime' || monthsValid;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Grant Athilio Pro
        </h3>
        <p className="text-sm text-gray-600 mb-1">Grant a Pro membership for:</p>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {user.dbUser.email}
        </p>
        {user.localSubscription ? (
          <p className="text-sm text-gray-500 mb-4">
            Current:{' '}
            <span className="font-medium text-gray-900">
              {user.localSubscription.planType} · {user.localSubscription.source}
            </span>
          </p>
        ) : (
          <p className="text-sm text-gray-500 mb-4">No current subscription</p>
        )}

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Plan type
        </label>
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="planType"
              value="monthly"
              checked={planType === 'monthly'}
              onChange={() => setPlanType('monthly')}
              disabled={isPending}
            />
            Monthly
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="planType"
              value="lifetime"
              checked={planType === 'lifetime'}
              onChange={() => setPlanType('lifetime')}
              disabled={isPending}
            />
            Lifetime
          </label>
        </div>

        {planType === 'monthly' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (months)
            </label>
            <input
              type="number"
              min={1}
              max={120}
              step={1}
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm mb-2"
              disabled={isPending}
            />
            <p className="text-xs text-gray-400 mb-6">
              Membership will be valid for {monthsValid ? parsedMonths : '—'}{' '}
              month{parsedMonths === 1 ? '' : 's'} from now.
            </p>
          </>
        )}
        {planType === 'lifetime' && (
          <p className="text-xs text-gray-400 mb-6">
            Lifetime membership never expires.
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(planType, parsedMonths)}
            disabled={isPending || !isValid}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {isPending ? 'Granting…' : 'Grant Pro'}
          </button>
        </div>
      </div>
    </div>
  );
}
